import { useEffect, useRef } from "react";
import { useRouter } from "next/router";

const STORAGE_KEY = "lang";

/*
 * Switching language means re-rendering the current route under the other
 * locale without jumping back to the top of the page, and remembering the
 * choice for the rest of the session. Three places offer the switch (desktop
 * top bar, mobile bar, user dropdown) and they all behave identically.
 */
export function useLanguageSwitcher() {
  const router = useRouter();
  const locale = router.locale;

  const switchLanguage = (nextLocale) => {
    if (nextLocale === locale) return;

    sessionStorage.setItem(STORAGE_KEY, nextLocale);

    /* Leave the pathname alone: Next adds the locale prefix itself. */
    const { pathname, query, asPath } = router;
    router.replace({ pathname, query }, asPath, {
      locale: nextLocale,
      scroll: false,
    });
  };

  const toggleLanguage = () => switchLanguage(locale === "en" ? "ar" : "en");

  return { locale, switchLanguage, toggleLanguage };
}

/*
 * A visitor who picked a language keeps it when they land on a URL for the
 * other one (a shared link, a bookmark). Runs once per mount, and only when
 * the stored choice disagrees with the URL.
 */
export function useStoredLanguageRedirect() {
  const { locale, switchLanguage } = useLanguageSwitcher();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored && stored !== locale) switchLanguage(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
